import { CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import { CountryBenefits, Maybe, BenefitTier, CalendarDurationType } from "@oysterhr/schema"
import { CalendarDurationLike } from "@oysterhr/common"
import type { TFunction } from "react-i18next"

export type CountryBenefitData = Record<BenefitTier, Benefit[]>

export type CountryBenefitDataCollection = Record<string, CountryBenefitData>

export interface Benefit {
  name: string
  note?: Maybe<string>
}

const createEmptyBenefitsObject = () =>
  ({
    [BenefitTier.Statutory]: [],
    [BenefitTier.Base]: [],
    [BenefitTier.Good]: [],
    [BenefitTier.Top]: [],
  }) as CountryBenefitData

export function packBenefits(
  benefitsData: Pick<CountryBenefits, "tier" | "comment" | "benefit">[],
): CountryBenefitData {
  const packedBenefits = createEmptyBenefitsObject()

  for (const { tier, comment, benefit } of benefitsData) {
    packedBenefits[tier].push({
      note: comment,
      name: benefit,
    })
  }

  return packedBenefits
}

export function useBenefitsOferringTranslations() {
  return useTranslation("BenefitsOffering")
}

export const TIERS_LIST = [
  BenefitTier.Statutory,
  BenefitTier.Base,
  BenefitTier.Good,
  BenefitTier.Top,
]

export function getDisplayedBenefitList(data: CountryBenefitData, tier: BenefitTier) {
  const benefits: (Benefit & { tier: BenefitTier })[] = []

  for (const currentTier of TIERS_LIST) {
    benefits.unshift(...data[currentTier].map(benefit => ({ ...benefit, tier: currentTier })))
    if (currentTier === tier) {
      return benefits
    }
  }

  return benefits
}

export function pickGridRowCount(elementsCount: number): CSSProperties {
  const halfElementsCount = Math.ceil(elementsCount / 2)
  return {
    gridTemplateRows: `repeat(${Math.max(halfElementsCount, 10)}, auto)`,
  }
}

export const timeWithUnit = (t: TFunction, time: CalendarDurationLike | null | undefined) => {
  if (!time) return ""
  if (time?.unit === CalendarDurationType.Infinite) return t("enums.timeAmounts.INFINITE")

  const unitTranslation = t(`enums.timeAmounts.${time.unit}`, {
    count: time.value || 0,
    ns: "salary_insights_tool/benefits",
  })
  return `${time.value} ${unitTranslation}`
}

export const multipleDurationsWithUnit = (
  t: TFunction,
  durationStart: CalendarDurationLike | null | undefined,
  durationEnd?: CalendarDurationLike | null | undefined,
) => {
  if (!durationEnd) return timeWithUnit(t, durationStart)

  if (durationStart?.unit !== durationEnd?.unit) {
    return `${timeWithUnit(t, durationStart)} - ${timeWithUnit(t, durationEnd)}`
  }

  const unitTranslation = t(`enums.timeAmounts.${durationStart.unit}`, {
    count: 0,
    ns: "salary_insights_tool/benefits",
  })

  return `${durationStart.value}-${durationEnd.value} ${unitTranslation}`
}
